import { useState, useEffect, useCallback } from 'react';
import { useAccount } from 'wagmi';
import { SUBSCRIPTION, isAdminWallet, API_BASE } from '@/lib/sovereign';
import { toast } from '@/hooks/use-toast';
import { apiFetch } from '@/lib/marketing/api';
import { tenantIdFromHost } from '@/lib/tenant-host';

export interface SubscriptionState {
  plan: 'free' | 'monthly' | 'lifetime';
  expiresAt: number | null;
  txHash: string | null;
  activatedAt: number;
}

export interface ServerSubscription {
  plan: string;
  expiresAt: number | null;
  priceLockedUSD: number | null;
  isIntro: boolean;
  hasLapsed: boolean;
  enrollmentCount: number;
  activatedAt: number | null;
  monthlyPrice: number;
  eligibleForIntro: boolean;
}

export type RenewalStatus = 'active' | 'reminder' | 'urgent' | 'grace' | 'expired' | 'lifetime' | 'free';

const STORAGE_KEY = (addr: string) => `sovereign_sub_${addr.toLowerCase()}`;
const DAY_MS = 24 * 60 * 60 * 1000;

function loadSub(address: string): SubscriptionState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(address));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SubscriptionState;
    if (parsed.plan === 'lifetime') return parsed;
    if (parsed.plan === 'monthly' && parsed.expiresAt) return parsed;
    return null;
  } catch {
    return null;
  }
}

function saveSub(address: string, sub: SubscriptionState) {
  localStorage.setItem(STORAGE_KEY(address), JSON.stringify(sub));
}

function computeRenewalStatus(sub: SubscriptionState | null): RenewalStatus {
  if (!sub) return 'free';
  if (sub.plan === 'lifetime') return 'lifetime';
  if (!sub.expiresAt) return 'free';

  const now = Date.now();
  const msUntilExpiry = sub.expiresAt - now;
  const daysUntilExpiry = msUntilExpiry / DAY_MS;

  if (daysUntilExpiry > SUBSCRIPTION.RENEWAL_REMINDER_DAYS) return 'active';
  if (daysUntilExpiry > SUBSCRIPTION.RENEWAL_URGENT_DAYS) return 'reminder';
  if (daysUntilExpiry > 0) return 'urgent';
  if (daysUntilExpiry > -SUBSCRIPTION.GRACE_PERIOD_DAYS) return 'grace';
  return 'expired';
}

export function useSubscription() {
  const { address, isConnected } = useAccount();
  const [sub, setSub] = useState<SubscriptionState | null>(null);
  const [serverSub, setServerSub] = useState<ServerSubscription | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!address) { setSub(null); setServerSub(null); return; }
    const existing = loadSub(address);
    setSub(existing);

    fetch(`${API_BASE}/api/v1/subscription/${address.toLowerCase()}`, { headers: { 'X-Tenant-ID': tenantIdFromHost() } })
      .then(r => r.json())
      .then((data: ServerSubscription) => {
        setServerSub(data);
        if (data.plan === 'monthly' || data.plan === 'lifetime') {
          const serverState: SubscriptionState = {
            plan: data.plan as 'monthly' | 'lifetime',
            expiresAt: data.expiresAt,
            txHash: null,
            activatedAt: data.activatedAt ?? Date.now(),
          };
          setSub(serverState);
          saveSub(address, serverState);
        } else {
          setSub(null);
          localStorage.removeItem(STORAGE_KEY(address));
        }
      })
      .catch(() => {
        setError('Unable to verify subscription status. Showing cached data.');
        toast({ title: 'Connection issue', description: 'Could not reach the server to check your subscription. Showing last known status.', variant: 'destructive' });
        if (existing && existing.plan === 'monthly' && existing.expiresAt) {
          if (existing.expiresAt < Date.now()) {
            setSub(null);
          }
        }
      });
  }, [address]);

  const isAdmin = isAdminWallet(address);
  const renewalStatus: RenewalStatus = isAdmin ? 'lifetime' : computeRenewalStatus(sub);
  const isInGrace = renewalStatus === 'grace';
  const isPremium = isAdmin || ['active', 'reminder', 'urgent', 'grace', 'lifetime'].includes(renewalStatus);

  const daysRemaining = sub?.plan === 'lifetime'
    ? Infinity
    : sub?.expiresAt
      ? Math.ceil((sub.expiresAt - Date.now()) / DAY_MS)
      : 0;

  const graceDaysRemaining = isInGrace && sub?.expiresAt
    ? Math.max(0, Math.ceil((sub.expiresAt + SUBSCRIPTION.GRACE_PERIOD_DAYS * DAY_MS - Date.now()) / DAY_MS))
    : 0;

  const monthlyPrice = serverSub?.monthlyPrice ?? (address ? SUBSCRIPTION.PLANS.MONTHLY.priceUSD : SUBSCRIPTION.PLANS.INTRO.priceUSD);
  const eligibleForIntro = serverSub?.eligibleForIntro ?? true;

  const activate = useCallback(async (planId: 'monthly' | 'lifetime' | 'annual', txHash: string, promoCode?: string, months?: number) => {
    if (!address) return;
    setVerifying(true);
    setError(null);

    const body: Record<string, string | number> = { txHash, planId };
    if (promoCode) body.promoCode = promoCode;
    if (planId === 'monthly' && months && months > 1) body.months = months;

    // apiFetch attaches the Privy JWT (Authorization: Bearer) and
    // X-Wallet-Address — required now that verify-payment is auth-gated.
    // On a 401 we retry once: brand-new subscribers can hit verify before
    // their Privy session is fully established. apiFetch fetches a fresh
    // JWT on every call, so the second attempt usually succeeds.
    const callVerify = () => apiFetch<{
      valid: boolean;
      error?: string;
      plan?: string;
      expiresAt?: number | null;
      isIntro?: boolean;
      priceApplied?: number;
    }>('/api/v1/verify-payment', { method: 'POST', body: JSON.stringify(body) });

    try {
      let data;
      try {
        data = await callVerify();
      } catch (firstErr) {
        const firstMsg = firstErr instanceof Error ? firstErr.message : String(firstErr);
        if (firstMsg.startsWith('401')) {
          // Auth not yet established — wait briefly for Privy to settle, then retry once.
          await new Promise(r => setTimeout(r, 1500));
          data = await callVerify();
        } else {
          throw firstErr;
        }
      }

      if (!data.valid) {
        // Surface the specific backend error reason instead of a generic toast.
        const backendMsg = data.error || 'Payment could not be verified.';
        const lower = backendMsg.toLowerCase();
        let title = 'Payment not confirmed';
        let userMsg = backendMsg;

        if (lower.includes('already')) {
          title = 'Already recorded';
          userMsg = `${backendMsg} Refresh the page to see your active subscription.`;
        } else if (lower.includes('recipient') || lower.includes('payment wallet') || lower.includes('no transfer') || lower.includes('no matching')) {
          title = 'Wrong transaction';
          userMsg = `This transaction doesn't appear to be a payment to Origin Sovereign. Did you paste a swap or unrelated transaction by mistake? The hash should be the one that sent LGNS, DAI, or USDT to the OS payment wallet.`;
        } else if (lower.includes('amount') || lower.includes('tier') || lower.includes('match')) {
          title = 'Amount mismatch';
          userMsg = `${backendMsg} Did you paste the wrong hash?`;
        } else if (lower.includes('not found')) {
          title = 'Transaction not found';
          userMsg = `${backendMsg} Wait 30 seconds and retry — or your subscription will activate automatically within 5 minutes once Polygon confirms the transaction.`;
        }

        setError(userMsg);
        toast({ title, description: userMsg, variant: 'destructive' });
        setVerifying(false);
        return false;
      }

      // Annual is stored as a monthly subscription with a 365-day term (see
      // DEPLOY-RUNBOOK.md "Known limitation") — so it displays as "Monthly".
      const storedPlan: SubscriptionState['plan'] = planId === 'annual' ? 'monthly' : planId;
      const fallbackExpiry = planId === 'lifetime'
        ? null
        : Date.now() + (planId === 'annual' ? 365 : (months ?? 1) * 30) * DAY_MS;

      const newSub: SubscriptionState = {
        plan: storedPlan,
        expiresAt: data.expiresAt ?? fallbackExpiry,
        txHash,
        activatedAt: Date.now(),
      };

      saveSub(address, newSub);
      setSub(newSub);

      if (serverSub) {
        setServerSub({
          ...serverSub,
          plan: storedPlan,
          expiresAt: newSub.expiresAt,
          isIntro: data.isIntro ?? false,
          monthlyPrice: data.priceApplied ?? monthlyPrice,
          eligibleForIntro: data.isIntro ?? false,
          enrollmentCount: (serverSub.enrollmentCount ?? 0) + 1,
        });
      }

      toast({ title: 'Premium activated!', description: 'Welcome to Origin Sovereign.' });
      setVerifying(false);
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // apiFetch throws `Error("STATUS STATUSTEXT: BODY")` — extract the
      // status code and the body keywords to produce a useful message.
      const statusMatch = msg.match(/^(\d{3})\s/);
      const status = statusMatch ? parseInt(statusMatch[1], 10) : 0;
      const lower = msg.toLowerCase();

      let title: string;
      let userMsg: string;

      if (status === 401) {
        title = 'Authentication issue';
        userMsg = 'Wallet authentication issue. Please reconnect your wallet and try again. Your payment is safe — the watcher will activate it automatically within 5 minutes.';
      } else if (lower.includes('already')) {
        title = 'Already recorded';
        userMsg = 'This transaction has already been recorded. Refresh the page to see your active subscription.';
      } else if (lower.includes('recipient') || lower.includes('payment wallet') || lower.includes('no transfer') || lower.includes('no matching')) {
        title = 'Wrong transaction';
        userMsg = `This transaction doesn't appear to be a payment to Origin Sovereign. Did you paste a swap or unrelated transaction by mistake? The hash should be the one that sent LGNS, DAI, or USDT to the OS payment wallet.`;
      } else if (lower.includes('amount') || lower.includes('tier')) {
        title = 'Amount mismatch';
        userMsg = `This transaction's amount doesn't match any plan tier. Did you paste the wrong hash?`;
      } else if (lower.includes('not found')) {
        title = 'Transaction not found';
        userMsg = 'Transaction not found on Polygon yet. Wait 30 seconds and retry, or your subscription will activate automatically within 5 minutes.';
      } else {
        title = 'Verification failed';
        userMsg = 'Network error verifying payment. Your payment is safe — the watcher will activate it automatically within 5 minutes. Refresh the page to check status.';
      }

      setError(userMsg);
      toast({ title, description: userMsg, variant: 'destructive' });
      setVerifying(false);
      return false;
    }
  }, [address, serverSub, monthlyPrice]);

  // Paymentless claim for zero-price promos (e.g. FIRSTMONTH). When a promo
  // prices the plan at exactly $0 there is no on-chain transfer to verify, so
  // the backend grants the month directly (gated by its per-account redemption
  // ledger). Mirrors activate()'s auth-retry and success handling.
  const claimFree = useCallback(async (promoCode: string) => {
    if (!address) return;
    setVerifying(true);
    setError(null);

    const callClaim = () => apiFetch<{
      valid: boolean;
      error?: string;
      plan?: string;
      expiresAt?: number | null;
    }>('/api/v1/claim-promo', { method: 'POST', body: JSON.stringify({ planId: 'monthly', promoCode }) });

    try {
      let data;
      try {
        data = await callClaim();
      } catch (firstErr) {
        const firstMsg = firstErr instanceof Error ? firstErr.message : String(firstErr);
        if (firstMsg.startsWith('401')) {
          await new Promise(r => setTimeout(r, 1500));
          data = await callClaim();
        } else {
          throw firstErr;
        }
      }

      if (!data.valid) {
        const backendMsg = data.error || 'Could not claim the promo.';
        const title = backendMsg.toLowerCase().includes('already') ? 'Already redeemed' : 'Claim failed';
        setError(backendMsg);
        toast({ title, description: backendMsg, variant: 'destructive' });
        setVerifying(false);
        return false;
      }

      const newSub: SubscriptionState = {
        plan: 'monthly',
        expiresAt: data.expiresAt ?? Date.now() + 30 * DAY_MS,
        txHash: null,
        activatedAt: Date.now(),
      };
      saveSub(address, newSub);
      setSub(newSub);

      if (serverSub) {
        setServerSub({
          ...serverSub,
          plan: 'monthly',
          expiresAt: newSub.expiresAt,
          enrollmentCount: (serverSub.enrollmentCount ?? 0) + 1,
        });
      }

      toast({ title: 'Free month claimed!', description: 'Welcome to Origin Sovereign Premium.' });
      setVerifying(false);
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      const userMsg = msg.startsWith('401')
        ? 'Wallet authentication issue. Please reconnect your wallet and try again.'
        : 'Network error claiming the promo. Please try again in a minute.';
      setError(userMsg);
      toast({ title: 'Claim failed', description: userMsg, variant: 'destructive' });
      setVerifying(false);
      return false;
    }
  }, [address, serverSub]);

  return {
    isConnected,
    address,
    isPremium,
    isAdmin,
    isInGrace,
    renewalStatus,
    plan: isAdmin ? 'admin' as const : (sub?.plan ?? 'free'),
    daysRemaining: isAdmin ? Infinity : daysRemaining,
    graceDaysRemaining,
    expiresAt: sub?.expiresAt ?? null,
    txHash: sub?.txHash ?? null,
    monthlyPrice,
    eligibleForIntro,
    hasLapsed: serverSub?.hasLapsed ?? false,
    enrollmentCount: serverSub?.enrollmentCount ?? 0,
    activate,
    claimFree,
    verifying,
    error,
  };
}
